/**
 * Shared, frontend-safe Alice type model for the Command Center Platform.
 *
 * Responsibilities (frontend-only):
 *   - Describe what the user is looking at (page + record context).
 *   - Describe context items that may be attached to an Alice turn.
 *   - Describe suggested actions and composer/draft sessions.
 *   - Describe per-portal Alice configuration (display + scoping metadata).
 *
 * This module MUST stay free of secrets and server-only logic. The actual
 * context assembly, record fetching, tool permission checks, approval gates,
 * token logging and provider routing happen server-side (see the Worker
 * copilot routes today, and the future Context Engine described in
 * docs/alice-actions-framework.md).
 */

export type AliceContextSourceType =
  | 'page'
  | 'form'
  | 'form_entry'
  | 'task'
  | 'time_entry'
  | 'organization'
  | 'contact'
  | 'communication'
  | 'email_thread'
  | 'invoice'
  | 'subscription'
  | 'payment'
  | 'agent'
  | 'call'
  | 'prompt'
  | 'proposal'
  | 'project'
  | 'website'
  | 'vault_document'
  | 'lead'
  | 'user'
  | 'manual';

/** Safe, read/draft-oriented capabilities Alice may use without elevated rights. */
export type AliceAllowedAction =
  | 'read_context'
  | 'draft_text'
  | 'suggest_field_values'
  | 'update_visible_draft_field'
  | 'open_center_workspace'
  | 'open_quick_action_drawer';

/** Sensitive capabilities that require an explicit approval gate (server-enforced). */
export type AliceBlockedAction =
  | 'send_email_without_approval'
  | 'delete_records'
  | 'publish_changes'
  | 'charge_client'
  | 'change_permissions'
  | 'access_unscoped_client_data';

export type AliceMode = 'global' | 'context' | 'composer';
export type PortalAliceDefaultState = 'open' | 'collapsed' | 'hidden';
export type PortalAliceScope = 'internal' | 'client' | 'sandbox';

/** Visual/grouping hint for a suggested action. */
export type AliceSuggestedActionCategory =
  | 'draft'
  | 'summarize'
  | 'navigate'
  | 'update'
  | 'analyze'
  | 'create';

export interface AliceContextItem {
  id: string;
  sourceType: AliceContextSourceType;
  recordId?: string;
  label: string;
  summary?: string;
  deepLink?: string;
  freshness?: string;
  permissionScope?: string[];
  /** The record the page is primarily about (e.g. the open task). */
  isPrimary?: boolean;
  /** Whether this item is currently attached to the next Alice turn. */
  isAttached?: boolean;
}

export interface AliceSuggestedAction {
  id: string;
  label: string;
  description?: string;
  /** The safe capability this suggestion exercises. */
  action: AliceAllowedAction;
  /** Which Alice mode the suggestion runs in. */
  mode?: AliceMode;
  /** Record types this suggestion is relevant to (used by the resolver). */
  sourceTypes?: AliceContextSourceType[];
  category?: AliceSuggestedActionCategory;
  icon?: string;
  /** Hint that running this requires a downstream approval gate. */
  requiresApproval?: boolean;
  /** Hint that selecting this opens a guarded Composer/draft session. */
  startsComposerSession?: boolean;
  /** When set, the suggestion is shown disabled with this reason. */
  disabledReason?: string;
}

export interface AlicePageContext {
  portalKey: string;
  portalScope?: PortalAliceScope;
  appKey?: string;
  pageKey: string;
  pageTitle: string;
  pageSummary?: string;
  recordType?: AliceContextSourceType;
  recordId?: string;
  recordLabel?: string;
  organizationId?: string;
  organizationLabel?: string;
  contextItems: AliceContextItem[];
  allowedActions: AliceAllowedAction[];
  blockedActions: AliceBlockedAction[];
  suggestedActions: AliceSuggestedAction[];
}

export interface PortalAliceConfig {
  enabled: boolean;
  defaultState: PortalAliceDefaultState;
  assistantName: string;
  assistantSubtitle?: string;
  avatarPath?: string;
  defaultModelLabel?: string;
  portalScope: PortalAliceScope;
  allowedContextSources: AliceContextSourceType[];
  allowedActions: AliceAllowedAction[];
  blockedActions: AliceBlockedAction[];
  enabledModes: AliceMode[];
  /** Composer target types this portal permits (omit = allow all). */
  enabledComposerTargets?: AliceComposerTargetType[];
  quickActionsEnabled: boolean;
  contextBarEnabled?: boolean;
  suggestedActionsEnabled?: boolean;
  voiceEnabledPlaceholder: boolean;
  fileUploadEnabledPlaceholder?: boolean;
  cameraEnabledPlaceholder?: boolean;
}

/* ------------------------------------------------------------------ *
 * Composer / draft session model (groundwork for guarded workflows).
 * ------------------------------------------------------------------ */

export type AliceSessionType = 'global' | 'context' | 'composer';

export type AliceComposerTargetType =
  | 'email_reply'
  | 'client_update'
  | 'task_note'
  | 'proposal_section'
  | 'billing_message'
  | 'call_summary'
  | 'generic';

export type AliceComposerStatus =
  | 'active'
  | 'awaiting_review'
  | 'approved'
  | 'discarded'
  | 'saved';

export interface AliceComposerSession {
  id: string;
  sessionType: 'composer';
  /** The suggested-action / platform-action key that started the session. */
  actionKey?: string;
  targetType: AliceComposerTargetType;
  targetRecordId?: string;
  targetRecordType?: AliceContextSourceType;
  /** Snapshot of page context at the moment the session started. */
  contextSnapshot?: AlicePageContext;
  draftText: string;
  status: AliceComposerStatus;
  dirty: boolean;
}

export interface CreateComposerSessionInput {
  targetType?: AliceComposerTargetType;
  actionKey?: string;
  targetRecordId?: string;
  targetRecordType?: AliceContextSourceType;
  contextSnapshot?: AlicePageContext;
  draftText?: string;
}

/** Maps a context record type to a sensible default Composer target. */
export function defaultComposerTargetForRecord(
  recordType?: AliceContextSourceType,
): AliceComposerTargetType {
  switch (recordType) {
    case 'communication':
    case 'email_thread':
      return 'email_reply';
    case 'task':
      return 'task_note';
    case 'proposal':
      return 'proposal_section';
    case 'invoice':
    case 'subscription':
    case 'payment':
      return 'billing_message';
    case 'call':
      return 'call_summary';
    case 'organization':
    case 'website':
    case 'project':
      return 'client_update';
    default:
      return 'generic';
  }
}

let composerSessionCounter = 0;

/** Creates a typed composer session. Pure/local — no persistence yet. */
export function createComposerSession(
  input: CreateComposerSessionInput = {},
): AliceComposerSession {
  composerSessionCounter += 1;
  const draftText = input.draftText ?? '';
  return {
    id: `composer-${Date.now()}-${composerSessionCounter}`,
    sessionType: 'composer',
    actionKey: input.actionKey,
    targetType:
      input.targetType ?? defaultComposerTargetForRecord(input.targetRecordType),
    targetRecordId: input.targetRecordId,
    targetRecordType: input.targetRecordType,
    contextSnapshot: input.contextSnapshot,
    draftText,
    status: 'active',
    dirty: draftText.trim().length > 0,
  };
}
